'use client'

import { CreditCard, Github, Menu, Terminal, Trophy, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useMemo } from 'react'

import logo from '@/assets/logo/logo.svg'
import { FetchUserBasicQuery } from '@/gql/graphql'
import { useTranslation } from '@/i18n/client'
import { cn } from '@/utils/cn'

import LinkIndicator from '../link/link-indicator'
import NavbarUserMenu from './navUserLite.new'

interface NavbarClientProps {
  isLoggedIn?: boolean
  user?: Pick<FetchUserBasicQuery['fetchUser'], 'name' | 'avatar' | 'login'>
}

function NavbarClient({ isLoggedIn, user }: NavbarClientProps) {
  const { t } = useTranslation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)
  const pathname = usePathname()
  const links = useMemo(() => {
    const result = [
      {
        href: '/',
        label: t('nav.home'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
        icon: (_props: any) => (
          <Image src={logo} alt='ShellTime Logo' width={32} height={32} />
        ),
        primary: pathname === '/',
      },
    ]
    if (user?.login) {
      result.push({
        href: `/users/${user?.login}/commands`,
        label: t('nav.commands'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        icon: (props: any) => <Terminal {...props} />,
        primary: /\/users\/[^/]+\/commands/.test(pathname),
      })
    }
    result.push(
      {
        href: '/ranking',
        label: t('nav.ranking'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        icon: (props: any) => <Trophy {...props} />,
        primary: pathname === '/ranking',
      },
      {
        href: '/plans',
        label: t('nav.plans'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        icon: (props: any) => <CreditCard {...props} />,
        primary: pathname === '/plans',
      }
    )
    return result
  }, [user, pathname, t])

  return (
    <>
      <div className='flex h-16 items-center justify-between'>
        {/* Logo and Desktop Navigation */}
        <nav className='hidden items-center gap-6 md:flex'>
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={cn(
                'items-center gap-2 transition-colors',
                'hidden md:flex', // Hide on mobile
                link.primary
                  ? 'font-bold text-blue-400 hover:text-blue-300'
                  : 'text-gray-300 hover:text-gray-100'
              )}
            >
              <LinkIndicator>
                <link.icon className='h-4 w-4' />
              </LinkIndicator>
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <button
          className='p-2 text-gray-400 hover:text-gray-300 md:hidden'
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? (
            <X className='h-6 w-6' />
          ) : (
            <Menu className='h-6 w-6' />
          )}
        </button>

        {/* User Menu or Login Button */}
        <div className='flex items-center gap-4'>
          {isLoggedIn && user ? (
            <NavbarUserMenu profile={user} githubLink='/auth/github' />
          ) : (
            <a
              href='/auth/github'
              className='flex items-center gap-2 rounded-lg bg-gray-800 px-4 py-2 text-white transition-colors hover:bg-gray-700'
            >
              <Github className='h-5 w-5' />
              <span className='hidden md:block'>{t('loginWithGithub')}</span>
            </a>
          )}
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className='border-t border-gray-800 animate-in fade-in-50 slide-in-from-bottom-2 md:hidden'>
          <nav className='space-y-2 py-4'>
            {links.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-4 py-2 transition-colors',
                  link.primary
                    ? 'font-bold text-blue-400 hover:text-blue-300'
                    : 'text-gray-300 hover:bg-gray-800'
                )}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LinkIndicator>
                  <link.icon className='h-4 w-4' />
                </LinkIndicator>
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </>
  )
}

export default NavbarClient
